/**
 * Range-selector buttons shared by the main chart and the ratio chart, so
 * their periods stay in sync (both visually and as the source of the
 * average/±1σ overlay window) without duplicating the button list between
 * the two chart config modules.
 */
export const RANGE_SELECTOR_BUTTONS = [
  { type: 'year', count: 5, text: '5y' },
  { type: 'year', count: 10, text: '10y' },
  { type: 'year', count: 15, text: '15y' },
  { type: 'year', count: 20, text: '20y' },
  { type: 'year', count: 25, text: '25y' },
  { type: 'year', count: 30, text: '30y' },
  { type: 'all', text: 'All' },
];

/**
 * Compact range-selector styling shared by both stock charts on narrow
 * screens. Return a fresh object so Highcharts can safely merge or mutate
 * either chart's options without affecting the other chart.
 */
export const getCompactRangeSelectorOptions = () => ({
  inputEnabled: false,
  buttonSpacing: 4,
  buttonTheme: {
    padding: 4,
    style: { fontSize: '11px' },
  },
  labelStyle: { fontSize: '11px' },
});

// Derived from RANGE_SELECTOR_BUTTONS itself (not a hardcoded magic number)
// so the "15y"/"All" defaults below can't silently drift out of sync with
// the button list again — the previous static default (`selected = 3`) was
// actually pointing at the "20y" button, not "15y", an off-by-one that
// pre-dates this fix.
const FIFTEEN_YEAR_BUTTON_INDEX = RANGE_SELECTOR_BUTTONS.findIndex(
  (b) => b.type === 'year' && b.count === 15
);
const ALL_BUTTON_INDEX = RANGE_SELECTOR_BUTTONS.findIndex((b) => b.type === 'all');

export const getRangeSelector = (selected = FIFTEEN_YEAR_BUTTON_INDEX) => ({
  selected,
  buttons: RANGE_SELECTOR_BUTTONS,
});

/**
 * Computes the main/ratio charts' adaptive default x-axis window for a
 * freshly fetched `price` series (`[ts, value][]`, chronological): the last
 * 15 years of data if at least 15 years of history exists, or the symbol's
 * full available history otherwise. Same "15Y capped, never more, full
 * history if shorter" principle as the Valuation card's adaptive horizon
 * search (see hooks/useValuationRegression.js), adapted from a whole-year
 * backtest-horizon search to a direct min/max timestamp window since this
 * only needs one comparison, not a search over candidate horizons.
 *
 * `estimateSeriesList` is the set of currently-relevant metric series whose
 * forecast/estimate tail should stay visible by default — the main chart's
 * selected metric plus the ratio chart's ticked metrics (see useStockData.js)
 * — since both charts' fair-value/ratio lines extend past `price`'s last
 * actual point into that forecast region (TTM bridge, analyst consensus —
 * see buildFairValueSeries/buildEstimatedPlotBand in seriesMath.js) rather
 * than stopping there. The right edge (`max`) grows to cover whichever of
 * those series reaches furthest forward, derived from the real data passed
 * in each call rather than a hardcoded assumption about how far estimates
 * extend; the left edge (`min`) is always 15 years back from price's own
 * last *actual* datapoint, never from the extended max, so a long forecast
 * tail can't silently widen the historical window too. Defaults to `[]` so
 * existing callers that don't have (or don't care about) an estimate tail
 * are unaffected.
 *
 * Returns `{ min, max, selected }`: `min`/`max` are timestamps for
 * `xAxis.setExtremes`, and `selected` is the RANGE_SELECTOR_BUTTONS index
 * whose fixed range matches the *actual-history* window (Highcharts itself
 * re-derives and highlights the truly-matching button from the real
 * extremes on redraw — see RangeSelector#updateButtonStates — so this is
 * mainly a reasonable initial hint, not load-bearing for correctness).
 * `price` with fewer than 1 point returns `{ min: null, max: null, selected:
 * ALL_BUTTON_INDEX }` — nothing to compute a window over, so this leaves the
 * axis unbounded exactly as before.
 */
export const computeDefaultRange = (price, estimateSeriesList = []) => {
  if (!price?.length) return { min: null, max: null, selected: ALL_BUTTON_INDEX };

  const firstTs = price[0][0];
  const lastTs = price[price.length - 1][0];

  const cutoff = new Date(lastTs);
  cutoff.setUTCFullYear(cutoff.getUTCFullYear() - 15);
  const cutoffTs = cutoff.getTime();

  // Grow the right edge to whichever relevant series' forecast tail reaches
  // furthest beyond price's last actual point (or leave it at `lastTs` when
  // none has forward coverage past price, e.g. fcf/ocf, or none were
  // passed at all).
  const maxTs = estimateSeriesList.reduce((furthest, series) => {
    if (!series?.length) return furthest;
    const seriesLastTs = series[series.length - 1][0];
    return seriesLastTs > furthest ? seriesLastTs : furthest;
  }, lastTs);

  // At least 15 years of real history: default to the last 15 years (of
  // actuals), extended by any forecast tail.
  if (firstTs <= cutoffTs) {
    return { min: cutoffTs, max: maxTs, selected: FIFTEEN_YEAR_BUTTON_INDEX };
  }
  // Less than 15 years of real history: default to all of it, not a 15Y
  // window that would clamp against data that doesn't exist — extended by
  // any forecast tail the same way.
  return { min: firstTs, max: maxTs, selected: ALL_BUTTON_INDEX };
};
